import { Component, Input, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AbcCore, CompareHelper } from '@barthauer/abc/core';
import { ICoreDataEntity } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { Subject, Subscription } from 'rxjs';
import { AbcArenaService, LayoutUsage } from '../../abc-arena.service';
import { ArenaService } from '../arena.service';
import { DataFormService } from '../data-form/data-form.service';
import { MenuButton } from '../shared/type';
import { LayoutViewModel } from '../viewModels/layout.viewModel';
import { menuAnimationComponent } from './menu.animation.component';
import { AdditionalMenuItem } from './menu.model';

@Component({
  selector: 'arena-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  animations: menuAnimationComponent,
  standalone: false,
})
export class MenuComponent implements OnInit {
  @ViewChild('AbcNotificationComponent', { static: true, read: ViewContainerRef }) abc: ViewContainerRef;

  @Input() isStandaloneDataForm: boolean;

  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  private _buttons: Map<MenuButton | string, AdditionalMenuItem> = new Map();
  private _buttonsBackup: Map<MenuButton | string, AdditionalMenuItem>;

  public buttons: AdditionalMenuItem[] = [];
  private onMenuClick: (menu: MenuButton) => Promise<boolean>;
  public layoutVM: LayoutViewModel;
  public fabTogglerState = 'inactive';
  public res = ResourceSet.Empty;
  public resAsync: Promise<ResourceSet>;
  public currentDataEntity: ICoreDataEntity;

  constructor(
    public arenaService: ArenaService,
    public iconRegistry: MatIconRegistry,
    public sanitizer: DomSanitizer,
    private abcArenaService: AbcArenaService,
    private resourceService: ResourceManagerService,
    private dataFormService: DataFormService,
    private abcCore: AbcCore,
  ) {
    this.resAsync = this.resourceService.getResourceSet('abc-arena-menu');
  }

  async ngOnInit() {
    this.res = await this.resAsync;
  }

  private isStandardMenuButtonsConfigurationSet: boolean = false;
  private async setStandardMenuButtonsConfiguration(): Promise<void> {
    if (!this.isStandardMenuButtonsConfigurationSet) {
      this._buttons.set(MenuButton.Delete, await this.deleteButtonConfiguration());
      this._buttons.set(MenuButton.Add, await this.addButtonConfiguration());
      this._buttons.set(MenuButton.Save, await this.saveButtonConfiguration());
      this._buttons.set(MenuButton.Undo, await this.undoButtonConfiguration());
      this._buttons.set(MenuButton.Report, await this.reportButtonConfiguration());
      this._buttons.set(MenuButton.DmlQuery, await this.dmlQueryButtonConfiguration());
      this.isStandardMenuButtonsConfigurationSet = true;
    }
  }

  private _additionalMenuItems: AdditionalMenuItem[] = [];
  public get additionalMenuItems(): AdditionalMenuItem[] {
    if (this._additionalMenuItems?.length && this.abcArenaService?.additionalMenuActions?.length) {
      const result: AdditionalMenuItem[] = [];
      result.push(...this.abcArenaService.additionalMenuActions);
      result.push(...this._additionalMenuItems);
      return result;
    } else if (this._additionalMenuItems?.length) {
      return this._additionalMenuItems;
    } else {
      return this.abcArenaService?.additionalMenuActions || [];
    }
  }

  private async setCustomMenuButtonsConfiguration(): Promise<void> {
    const origKeys = Array.from(this._buttons.keys());
    if (this._buttonsBackup) {
      this._buttons = new Map(this._buttonsBackup);
    }
    if (this.additionalMenuItems?.length) {
      if (!this._buttonsBackup) {
        this._buttonsBackup = new Map(this._buttons);
      }
      for (const menu of this.additionalMenuItems) {
        this._buttons.set(menu.key, menu);
      }
    }

    if (this.buttons?.length && !CompareHelper.areDeepEqual(origKeys, Array.from(this._buttons.keys()))) {
      this.onToggleFab();
    }
  }

  private sortButtonsByVisibleIndex() {
    this._buttons = new Map([...this._buttons.entries()].sort((a, b) => a[1].visibleIndex - b[1].visibleIndex));
  }

  private async deleteButtonConfiguration(): Promise<AdditionalMenuItem> {
    const btn: AdditionalMenuItem = new AdditionalMenuItem();
    btn.icon = (await this.resAsync).svgName('Delete-Image');
    btn.deny = false;
    btn.name = 'delete';
    btn.readOnly = false;
    btn.disabled = false;
    btn.tooltip = this.arenaService.res('Menu-Delete');
    btn.visibleIndex = 3;
    btn.onClick = () => this.deleteEditedObject();
    return btn;
  }

  private async addButtonConfiguration(): Promise<AdditionalMenuItem> {
    const btn: AdditionalMenuItem = new AdditionalMenuItem();
    btn.icon = (await this.resAsync).svgName('Add-Image');
    btn.deny = false;
    btn.name = 'add';
    btn.readOnly = false;
    btn.disabled = false;
    btn.tooltip = this.arenaService.res('Menu-Add');
    btn.visibleIndex = 2;
    btn.onClick = () => this.addNewObject();
    return btn;
  }

  private async saveButtonConfiguration(): Promise<AdditionalMenuItem> {
    const btn: AdditionalMenuItem = new AdditionalMenuItem();
    btn.icon = (await this.resAsync).svgName('Save-Image');
    btn.deny = false;
    btn.name = 'save';
    btn.readOnly = false;
    btn.disabled = true;
    btn.tooltip = this.arenaService.res('Menu-Save');
    btn.visibleIndex = 0;
    btn.onClick = () => this.saveEditedObject();
    return btn;
  }

  private async undoButtonConfiguration(): Promise<AdditionalMenuItem> {
    const btn: AdditionalMenuItem = new AdditionalMenuItem();
    btn.icon = (await this.resAsync).svgName('Undo-Image');
    btn.deny = false;
    btn.name = 'undo';
    btn.readOnly = false;
    btn.disabled = true;
    btn.tooltip = this.arenaService.res('Menu-Undo');
    btn.visibleIndex = 1;
    btn.onClick = () => this.undoEditedObject();
    return btn;
  }

  private async reportButtonConfiguration(): Promise<AdditionalMenuItem> {
    const btn: AdditionalMenuItem = new AdditionalMenuItem();
    btn.icon = (await this.resAsync).svgName('Report-Image');
    btn.deny = false;
    btn.name = 'report';
    btn.readOnly = false;
    btn.disabled = true;
    btn.tooltip = this.arenaService.res('Menu-Report');
    btn.visibleIndex = 4;
    btn.beginGroup = true;
    btn.onClick = () => this.onMenuClick(MenuButton.Report);
    return btn;
  }

  private async dmlQueryButtonConfiguration(): Promise<AdditionalMenuItem> {
    const btn: AdditionalMenuItem = new AdditionalMenuItem();
    btn.icon = (await this.resAsync).svgName('DmlQuery-Image');
    btn.deny = false;
    btn.name = 'dmlquery';
    btn.readOnly = false;
    btn.disabled = true;
    btn.tooltip = this.arenaService.res('Menu-DmlQuery');
    btn.visibleIndex = 5;
    btn.onClick = () => this.onMenuClick(MenuButton.DmlQuery);
    return btn;
  }

  public async loadLayout(
    layoutViewModel: LayoutViewModel,
    isDataFormOnly: boolean,
    denyInsert: boolean,
    onMenuClick: (menu: MenuButton) => Promise<boolean>,
    additionalMenuItems: AdditionalMenuItem[],
  ): Promise<void> {
    this.layoutVM = layoutViewModel;
    this._additionalMenuItems = additionalMenuItems;
    this.onMenuClick = onMenuClick;

    await this.setStandardMenuButtonsConfiguration();
    await this.setCustomMenuButtonsConfiguration();
    this.sortButtonsByVisibleIndex();

    this._buttons.get(MenuButton.Add).deny =
      denyInsert || layoutViewModel?.denyInsert || layoutViewModel?.isReadOnly || isDataFormOnly;

    this._buttons.get(MenuButton.Delete).deny =
      layoutViewModel?.denyDelete || layoutViewModel?.isReadOnly || isDataFormOnly;

    this._buttons.get(MenuButton.Save).deny = layoutViewModel?.denyUpdate || layoutViewModel?.isReadOnly;

    this._buttons.get(MenuButton.Report).deny =
      isDataFormOnly ||
      (this.abcArenaService.actions?.getUIConfiguration()?.isReportingDisabled &&
        this.abcArenaService.actions?.getUIConfiguration()?.isQueryingDisabled);
    this._buttons.get(MenuButton.DmlQuery).deny =
      isDataFormOnly ||
      layoutViewModel?.isReadOnly ||
      this.abcArenaService.actions?.getUIConfiguration()?.isQueryingDisabled;

    const otvm = await this.abcCore.objectSpace.getObjectType(layoutViewModel?.objectType).toPromise();

    if (otvm) {
      this.currentDataEntity = {
        Id: null,
        ObjectType: otvm.name,
      };

      if (this.additionalMenuItems?.length) {
        for (const menu of this.additionalMenuItems) {
          if (menu?.subCollectionName) {
            this._buttons.get(menu.key).deny = !otvm.referencingByName.has(menu.subCollectionName);
          }
        }
      }
    }

    let lastBeginGroup: boolean = false;
    for (const e of Array.from(this._buttons)) {
      const btn = e[1];
      if (CompareHelper.isNullorEmpty(btn['originalBeginGroup'])) {
        btn['originalBeginGroup'] = !!btn.beginGroup;
      } else {
        btn.beginGroup = btn['originalBeginGroup'];
      }
      if (btn.deny) {
        if (btn.beginGroup) {
          lastBeginGroup = true;
        }
      } else {
        if (lastBeginGroup) {
          btn.beginGroup = true;
        }
        lastBeginGroup = false;
      }
    }
  }

  public async setCurrentObject(object: ICoreDataEntity): Promise<void> {
    if (object) {
      if (object.Id && this.currentDataEntity) {
        this.currentDataEntity.Id = object.Id;

        this.enableMenuButtons([MenuButton.Delete, MenuButton.Report, MenuButton.DmlQuery]);
        if (!this.isStandaloneDataForm) {
          this.enableMenuButtons([MenuButton.Add]);
        }
      } else {
        this.disableMenuButtons([MenuButton.Delete, MenuButton.Report, MenuButton.DmlQuery]);
        this.disableMenuButtons([MenuButton.Add]);
      }
      if (this.arenaService.actions.getContext()?.layoutUsage === LayoutUsage.New) {
        if (this.isStandaloneDataForm) {
          this.onToggleFab();
          this.disableMenuButtons([MenuButton.Undo]);
        } else {
          this.enableMenuButtons([MenuButton.Undo]);
        }
      }
    } else {
      this.disableMenuButtons([MenuButton.Delete, MenuButton.Report, MenuButton.DmlQuery]);
      if (!this.isStandaloneDataForm) {
        this.enableMenuButtons([MenuButton.Add]);
      }
      this.disableMenuButtons([MenuButton.Undo]);
    }
    this.currentlyHasChanges = undefined;

    if (this.additionalMenuItems?.length) {
      const ot = await this.abcCore.objectSpace.getObjectType(this.layoutVM?.objectType).toPromise();
      for (const element of this.additionalMenuItems) {
        element.disabled = true;
        if (object?.Id) {
          if (element?.subCollectionName) {
            if (ot?.referencingByName.has(element?.subCollectionName)) {
              element.disabled = false;
            }
          } else {
            element.disabled = false;
          }
        }
      }
    }
  }

  private async saveEditedObject() {
    const success: boolean = await this.onMenuClick(MenuButton.Save);

    if (success) {
      this.disableMenuButtons([MenuButton.Undo, MenuButton.Save]);
    }
  }

  private async addNewObject() {
    const success: boolean = await this.onMenuClick(MenuButton.Add);
    if (success) {
      this.enableMenuButtons([MenuButton.Undo, MenuButton.Save]);
    }
  }

  private async undoEditedObject() {
    const success: boolean = await this.onMenuClick(MenuButton.Undo);
    if (success) {
      this.disableMenuButtons([MenuButton.Undo, MenuButton.Save]);
      if (!this.isStandaloneDataForm) {
        this.enableMenuButtons([MenuButton.Add]);
      }
    }
  }

  private async deleteEditedObject() {
    const success: boolean = await this.onMenuClick(MenuButton.Delete);
    if (success) {
      this.disableMenuButtons([
        MenuButton.Undo,
        MenuButton.Save,
        MenuButton.Delete,
        MenuButton.Report,
        MenuButton.DmlQuery,
      ]);
    }
  }

  onToggleFab() {
    if (this.buttons.length > 0) {
      this.fabTogglerState = 'inactive';
      this.buttons = [];
    } else {
      this.fabTogglerState = 'active';
      for (const e of Array.from(this._buttons)) {
        const type = e[0];
        const btn = e[1];
        this.buttons.push(btn);
      }
    }
  }

  private disableMenuButtons(items: MenuButton[]) {
    if (items.length !== 0) {
      items.forEach((item) => {
        if (this._buttons.has(item)) {
          this._buttons.get(item).disabled = true;
        }
      });
    }
  }

  private enableMenuButtons(items: MenuButton[]) {
    if (items.length !== 0) {
      items.forEach((item) => {
        if (this._buttons.has(item)) {
          this._buttons.get(item).disabled = false;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public notifyChanges(hasChanges: boolean): void {
    const isNewObject: boolean = this.arenaService.actions.getContext()?.layoutUsage === LayoutUsage.New;
    if ((hasChanges || isNewObject) && this.abcArenaService.currentDataEntity) {
      if (this.isStandaloneDataForm && isNewObject) {
        this.enableMenuButtons([MenuButton.Save]);
      } else {
        this.enableMenuButtons([MenuButton.Save, MenuButton.Undo]);
      }
    } else {
      this.disableMenuButtons([MenuButton.Save, MenuButton.Undo]);
    }
  }

  private currentlyHasChanges: boolean;
  public get hasChanges(): boolean {
    return (
      this.abcArenaService.currentDataEntity &&
      (this.currentlyHasChanges || this._buttons.get(MenuButton.Save)?.disabled === false)
    );
  }

  public ensureExpanded(): void {
    if (!this.buttons?.length) {
      this.onToggleFab();
    }
    if (this.hasChanges) {
      this.currentlyHasChanges = true;
      this.disableMenuButtons([MenuButton.Save, MenuButton.Undo]);
      setTimeout(() => {
        this.enableMenuButtons([MenuButton.Save, MenuButton.Undo]);
        this.currentlyHasChanges = undefined;
      }, 750);
    }
  }
}
